import type { Category } from '@shopgnd/shared';
import { compareCategoryOrder } from '@/lib/categoryTree';
import { PENDING_CATEGORY_ID } from '@/lib/productListing';

const FLASH_DEAL_PATTERN = /flash\s*deal/i;

/** Pro trade categories (Find a Pro) — ids are cat-pro-*; slugs are human-readable. */
export function isProTradeCategory(category: Category): boolean {
  return category.id.startsWith('cat-pro');
}

export function listProTradeCategories(categories: Category[]): Category[] {
  return categories.filter(isProTradeCategory);
}

/** Product departments for shop browse — not pro trades, flash deals, or internal moderation buckets. */
export function isShopDepartmentCategory(category: Category): boolean {
  if (category.id === PENDING_CATEGORY_ID || category.slug === 'pending-review') return false;
  if (isProTradeCategory(category)) return false;
  if (FLASH_DEAL_PATTERN.test(category.name)) return false;
  if (FLASH_DEAL_PATTERN.test(category.slug)) return false;
  if (category.slug === 'deals' || category.slug === 'flash-deals') return false;
  return true;
}

export function listShopDepartmentCategories(categories: Category[]): Category[] {
  return categories.filter(isShopDepartmentCategory);
}

/** Top-level shop departments (no parent) — home tiles, mega menu roots. */
export function listShopRootCategories(categories: Category[]): Category[] {
  return listShopDepartmentCategories(categories).filter((c) => !c.parentId);
}

const MAX_HOME_FEATURED = 3;
const MAX_SHOP_NAV = 8;


/** Admin-curated categories for the home page tile row (ranks 1–3). */
export function listHomeFeaturedCategories(categories: Category[]): Category[] {
  return listShopDepartmentCategories(categories)
    .filter((c) => c.homeFeaturedRank != null && c.homeFeaturedRank >= 1)
    .sort((a, b) => (a.homeFeaturedRank ?? 99) - (b.homeFeaturedRank ?? 99))
    .slice(0, MAX_HOME_FEATURED);
}

/** Admin-curated categories for header nav + shop category strip. */
export function listShopNavCategories(categories: Category[]): Category[] {
  return listShopDepartmentCategories(categories)
    .filter((c) => c.shopNavRank != null && c.shopNavRank >= 1)
    .sort((a, b) => (a.shopNavRank ?? 99) - (b.shopNavRank ?? 99))
    .slice(0, MAX_SHOP_NAV);
}

export function hasShopNavSelection(categories: Category[]): boolean {
  return listShopNavCategories(categories).length > 0;
}

/** Header nav + shop strip when admin has not picked nav categories yet. */
export function listDefaultShopNavCategories(categories: Category[]): Category[] {
  return listShopRootCategories(categories).sort(compareCategoryOrder).slice(0, MAX_SHOP_NAV);
}

/** Categories shown in header nav and shop strip (curated or stable default). */
export function resolveShopNavCategories(categories: Category[]): Category[] {
  const curated = listShopNavCategories(categories);
  return curated.length > 0 ? curated : listDefaultShopNavCategories(categories);
}

export function canFeatureCategoryOnHome(category: Category): boolean {
  return isShopDepartmentCategory(category);
}

export function canFeatureCategoryInShopNav(category: Category): boolean {
  return isShopDepartmentCategory(category);
}

export { MAX_HOME_FEATURED, MAX_SHOP_NAV };
